<template>
  <div class="mt-5">
    <div
      v-if="isDoctorLoading"
      ref="isLoading"
      class="text-center text-muted vertical-centered"
    >
      <font-awesome-icon
        :icon="['fas', 'circle-notch']"
        class="fa-spin fa-5x my-4"
      />
      <small class="d-block">
        CARGANDO
      </small>
    </div>
    <div
      v-else-if="doctor && Object.entries(doctor).length && doctor.fname"
      ref="elseIfDoctorLength"
      class="user-profile"
      :style="`transition: all .5s ease;background-color: ${bgColor
      }; color: ${txtColor};`"
    >
      <div class="container">
        <main class="user-personal-info">
          <img
            class="rounded"
            :src="imgSrc"
            :alt="`Perfil de ${personNames.fullName}`"
          >
          <header class="mt-3 mt-md-0 ml-md-3">
            <h1 class="fs-12">
              <span
                v-if="doctor.isBusiness"
                class="mt-1"
              >
                {{ doctor.fname }}
              </span>
              <template v-else>
                <span class="mt-1">
                  {{ personNames.names }}
                </span>
                <span
                  v-if="personNames.lnames"
                  class="mt-1"
                >
                  {{ personNames.lnames }}
                </span>
              </template>
            </h1>
            <p
              v-if="doctor.profileInfo && doctor.profileInfo.subname"
              class="h4-responsive font-weight-bold mb-0"
            >
              {{ doctor.profileInfo.subname }}
            </p>
            <p class="font-italic">
              <span>{{ searchTags() }}</span>
            </p>
          </header>
        </main>
        <div
          :class="['text-center my-3 ',
                   { 'animated shake delay-5s': addBtnCitaAnimation }]"
        >
          <mdb-btn
            ref="mdbBtnShoaAgendarCita"
            class="amii-agendar-btn"
            icon="calendar-alt"
            :disabled="Boolean(doctor.profileInfo &&
              doctor.profileInfo.agendaBtnDis)"
            @click="showAgendarCita = true"
          >
            {{ (doctor.profileInfo && doctor.profileInfo.buttonLabel)
              || 'Solicitar cita' }}
          </mdb-btn>
          <br>
          <small
            v-if="doctor.profileInfo && doctor.profileInfo.agendaBtnDis"
            class="text-muted"
          >Agenda desactivada</small>
        </div>

        <SeccionEnlaces
          v-if="doctor.profileInfo && doctor.profileInfo.buttonsFirst"
          :info="doctor"
        />

        <!-- CSS del perfil -->
        <component :is="'style'">
          {{ cssContent }}
        </component>
        <!-- HTML del perfil -->
        <!-- eslint-disable vue/no-v-html -->
        <div
          id="wrapper"
          ref="wrapper"
          class="page-content"
          v-html="htmlContent"
        />
        <SeccionEnlaces
          v-if="!doctor.profileInfo || !doctor.profileInfo.buttonsFirst"
          :info="doctor"
        />

        <!-- Sección de Opiniones y Valoraciones -->
        <section
          v-if="!doctor.profileInfo || !doctor.profileInfo.hideReviews"
          class="reviews-section mt-5 pt-4"
          :style="reviewsSectionStyle"
        >
          <div class="reviews-header text-center mb-4">
            <h2
              class="h3-responsive font-weight-bold mb-1"
              :style="{ color: txtColor }"
            >
              Opiniones y Valoraciones
            </h2>
            <p
              class="small mb-0"
              :style="{ color: txtColor, opacity: 0.75 }"
            >
              Experiencias de pacientes verificados
            </p>
          </div>

          <!-- Loading state -->
          <div
            v-if="isReviewsLoading &&
              (!doctorReviews || !doctorReviews.reviews)"
            class="text-center py-4 text-muted"
          >
            <font-awesome-icon
              :icon="['fas', 'circle-notch']"
              class="fa-spin fa-2x mb-2"
            />
            <p class="small">
              Cargando opiniones...
            </p>
          </div>

          <!-- Empty state -->
          <div
            v-else-if="!doctorReviews || doctorReviews.totalCount === 0"
            class="empty-reviews text-center py-5 px-3 rounded"
            :style="reviewsCardStyle"
          >
            <div class="empty-icon mb-3 text-warning">
              <font-awesome-icon
                :icon="['far', 'star']"
                size="3x"
              />
            </div>
            <h4
              class="h5-responsive font-weight-bold mb-2"
              :style="{ color: txtColor }"
            >
              Sin valoraciones aún
            </h4>
            <p
              class="mb-0 mx-auto"
              :style="{
                color: txtColor,
                opacity: 0.75,
                maxWidth: '500px'
              }"
            >
              Aún no hay opiniones registradas para este perfil.
              ¡Sé el primero en valorar tu consulta!
            </p>
          </div>

          <!-- Summary & List -->
          <div v-else>
            <!-- Rating Breakdown Card -->
            <div
              class="rating-overview-card p-4 rounded mb-4"
              :style="reviewsCardStyle"
            >
              <div class="row align-items-center">
                <!-- Left: Big Average & Stars -->
                <div
                  class="col-12 col-md-5 text-center mb-3 mb-md-0
                    border-md-right"
                >
                  <div
                    class="display-3 font-weight-bold rating-big-num"
                    :style="{ color: bigRatingColor }"
                  >
                    {{ (doctorReviews.average || 0).toFixed(1) }}
                  </div>
                  <div class="stars-row my-2 text-warning">
                    <font-awesome-icon
                      v-for="s in 5"
                      :key="s"
                      :icon="getStarIcon(doctorReviews.average, s)"
                      class="mx-1"
                    />
                  </div>
                  <div
                    class="small font-weight-bold"
                    :style="{ color: txtColor, opacity: 0.8 }"
                  >
                    {{ doctorReviews.totalCount }}
                    {{ doctorReviews.totalCount === 1
                      ? 'opinión' : 'opiniones' }}
                  </div>
                </div>

                <!-- Right: Star Distribution Bars (5 down to 1) -->
                <div class="col-12 col-md-7">
                  <div
                    v-for="star in [5, 4, 3, 2, 1]"
                    :key="star"
                    class="d-flex align-items-center mb-2 star-bar-row"
                  >
                    <span
                      class="star-label small mr-2"
                      :style="{ color: txtColor, opacity: 0.85 }"
                    >
                      {{ star }}
                      <font-awesome-icon
                        :icon="['fas', 'star']"
                        class="text-warning small"
                      />
                    </span>
                    <div
                      class="progress flex-grow-1 mr-2 star-progress"
                      :style="{ backgroundColor: starTrackBg }"
                    >
                      <div
                        class="progress-bar bg-warning"
                        role="progressbar"
                        :style="{
                          width: `${getDistributionPercent(star)}%`
                        }"
                        :aria-valuenow="getDistributionPercent(star)"
                        aria-valuemin="0"
                        aria-valuemax="100"
                      />
                    </div>
                    <span
                      class="star-percent small text-right"
                      :style="{ color: txtColor, opacity: 0.85 }"
                    >
                      {{ getDistributionCount(star) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Subcriteria Badges -->
              <div
                v-if="hasSubcriteriaAverages"
                class="subcriteria-row row mt-3 pt-3
                  text-center justify-content-around"
                :style="{ borderTop: `1px solid ${reviewsBorderColor}` }"
              >
                <div
                  v-if="doctorReviews.avgCalidadAtencion"
                  class="col-6 col-sm-3 mb-2"
                >
                  <div
                    class="subcriteria-badge p-2 rounded"
                    :style="subcriteriaBadgeStyle"
                  >
                    <small
                      class="d-block"
                      :style="{ color: txtColor, opacity: 0.75 }"
                    >
                      Calidad de atención
                    </small>
                    <strong
                      class="font-weight-bold"
                      :style="{ color: bigRatingColor }"
                    >
                      {{ doctorReviews.avgCalidadAtencion.toFixed(1) }} / 3
                    </strong>
                  </div>
                </div>
                <div
                  v-if="doctorReviews.avgComodidadEspacios"
                  class="col-6 col-sm-3 mb-2"
                >
                  <div
                    class="subcriteria-badge p-2 rounded"
                    :style="subcriteriaBadgeStyle"
                  >
                    <small
                      class="d-block"
                      :style="{ color: txtColor, opacity: 0.75 }"
                    >
                      Comodidad de espacios
                    </small>
                    <strong
                      class="font-weight-bold"
                      :style="{ color: bigRatingColor }"
                    >
                      {{ doctorReviews.avgComodidadEspacios.toFixed(1) }} / 3
                    </strong>
                  </div>
                </div>
                <div
                  v-if="doctorReviews.avgTiempoEspera"
                  class="col-6 col-sm-3 mb-2"
                >
                  <div
                    class="subcriteria-badge p-2 rounded"
                    :style="subcriteriaBadgeStyle"
                  >
                    <small
                      class="d-block"
                      :style="{ color: txtColor, opacity: 0.75 }"
                    >
                      Tiempo de espera
                    </small>
                    <strong
                      class="font-weight-bold"
                      :style="{ color: bigRatingColor }"
                    >
                      {{ doctorReviews.avgTiempoEspera.toFixed(1) }} / 3
                    </strong>
                  </div>
                </div>
                <div
                  v-if="doctorReviews.avgPrecisionResultado"
                  class="col-6 col-sm-3 mb-2"
                >
                  <div
                    class="subcriteria-badge p-2 rounded"
                    :style="subcriteriaBadgeStyle"
                  >
                    <small
                      class="d-block"
                      :style="{ color: txtColor, opacity: 0.75 }"
                    >
                      Precisión de resultados
                    </small>
                    <strong
                      class="font-weight-bold"
                      :style="{ color: bigRatingColor }"
                    >
                      {{ doctorReviews.avgPrecisionResultado.toFixed(1) }} / 3
                    </strong>
                  </div>
                </div>
              </div>
            </div>

            <!-- Reviews List -->
            <div class="reviews-list">
              <div
                v-for="rev in (doctorReviews.reviews || [])"
                :key="rev.id"
                class="review-item p-3 rounded mb-3"
                :style="reviewsCardStyle"
              >
                <div
                  class="d-flex justify-content-between align-items-start
                    flex-wrap mb-2"
                >
                  <div class="review-author d-flex align-items-center">
                    <div
                      class="avatar-author rounded-circle d-flex
                        align-items-center justify-content-center mr-2"
                      :style="authorAvatarStyle"
                    >
                      {{ (rev.authorName || 'P').charAt(0).toUpperCase() }}
                    </div>
                    <div>
                      <strong
                        class="d-block"
                        :style="{ color: txtColor }"
                      >
                        {{ rev.authorName || 'Paciente verificado' }}
                      </strong>
                      <div class="review-stars text-warning small">
                        <font-awesome-icon
                          v-for="s in 5"
                          :key="s"
                          :icon="s <= rev.rate
                            ? ['fas', 'star']
                            : ['far', 'star']"
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    class="review-date small mt-1 mt-sm-0"
                    :style="{ color: txtColor, opacity: 0.75 }"
                  >
                    {{ formatReviewDate(rev.createdAt) }}
                  </div>
                </div>

                <p
                  v-if="rev.comment"
                  class="review-comment mb-2"
                  :style="{ color: txtColor, opacity: 0.95 }"
                >
                  {{ rev.comment }}
                </p>

                <!-- Subcriteria tags for individual review -->
                <div
                  v-if="hasReviewSubcriteria(rev)"
                  class="review-subcriteria-tags d-flex flex-wrap gap-1 mt-2"
                >
                  <span
                    v-if="rev.calidadAtencion"
                    class="badge mr-1 mb-1"
                    :style="reviewChipStyle"
                  >
                    Atención: {{ rev.calidadAtencion }}/3
                  </span>
                  <span
                    v-if="rev.comodidadEspacios"
                    class="badge mr-1 mb-1"
                    :style="reviewChipStyle"
                  >
                    Espacios: {{ rev.comodidadEspacios }}/3
                  </span>
                  <span
                    v-if="rev.tiempoEspera"
                    class="badge mr-1 mb-1"
                    :style="reviewChipStyle"
                  >
                    Espera: {{ rev.tiempoEspera }}/3
                  </span>
                  <span
                    v-if="rev.precisionResultado"
                    class="badge mr-1 mb-1"
                    :style="reviewChipStyle"
                  >
                    Precisión: {{ rev.precisionResultado }}/3
                  </span>
                </div>
              </div>
            </div>

            <!-- Pagination / Ver más -->
            <div
              v-if="doctorReviews.totalCount >
                (doctorReviews.reviews && doctorReviews.reviews.length)"
              class="text-center mt-4"
            >
              <mdb-btn
                type="button"
                rounded
                :disabled="loadingMoreReviews"
                :style="loadMoreBtnStyle"
                class="font-weight-bold px-4 py-2"
                @click="loadMoreReviews"
              >
                <font-awesome-icon
                  v-if="loadingMoreReviews"
                  :icon="['fas', 'circle-notch']"
                  class="fa-spin mr-1"
                />
                Ver más opiniones
              </mdb-btn>
            </div>
          </div>
        </section>

        <!-- Ally Branding / Patrocinador Aliado -->
        <section
          v-if="allyBranding && (allyBranding.logoUrl
            || allyBranding.promoText)"
          class="section-ally-branding text-center py-4 my-2"
        >
          <div class="d-inline-flex flex-column align-items-center">
            <a
              v-if="allyBranding.promoUrl"
              :href="allyBranding.promoUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="d-inline-flex flex-column align-items-center
                text-decoration-none"
              :style="{ color: txtColor || 'inherit' }"
            >
              <img
                v-if="allyBranding.logoUrl"
                :src="allyBranding.logoUrl"
                :alt="allyBranding.allyName || 'Aliado'"
                class="ally-branding-logo mb-2"
              >
              <span
                v-if="allyBranding.promoText"
                class="ally-branding-text small font-weight-bold"
                :style="{ color: txtColor || '#64748b' }"
              >
                {{ allyBranding.promoText }}
              </span>
            </a>
            <div
              v-else
              class="d-inline-flex flex-column align-items-center"
            >
              <img
                v-if="allyBranding.logoUrl"
                :src="allyBranding.logoUrl"
                :alt="allyBranding.allyName || 'Aliado'"
                class="ally-branding-logo mb-2"
              >
              <span
                v-if="allyBranding.promoText"
                class="ally-branding-text small font-weight-bold"
                :style="{ color: txtColor || '#64748b' }"
              >
                {{ allyBranding.promoText }}
              </span>
            </div>
          </div>
        </section>
      </div>
    </div>
    <div
      v-else
      class="py-4 container vertical-centered text-muted"
    >
      <font-awesome-icon
        :icon="['fas', 'user-slash']"
        class="my-2 fa-5x"
      />
      <header class="h2-responsive font-weight-bold">
        ¡Lo sentimos!
      </header>
      <p class="lead text-center">
        <span class="d-sm-block">
          El perfil que buscas no ha sido configurado
        </span>
        <span class="d-sm-block">
          o está mal escrito el enlace.
        </span>
      </p>
      <p class="text-center">
        Búscalo a través de nuestro directorio médico y
        <span class="d-sm-block">
          descubre más especialistas en todas las áreas.
        </span>
      </p>
      <router-link
        v-slot="{ navigate }"
        :to="{ name: 'home' }"
        custom
      >
        <span
          role="link"
          class="btn btn-primary-base px-3"
          @click="navigate"
          @keypress.enter="navigate"
        >
          <font-awesome-icon
            icon="chevron-left"
            class="mr-1"
          />
          Ver directorio médico
        </span>
      </router-link>

      <p class="h3-responsive font-weight-bold my-2">
        ¿Eres el médico o administras una institución?
      </p>
      <p class="text-sm-center">
        Te invitamos a crear tu perfil público a través del sistema,
        <span class="d-sm-block">
          en la sección de Configuración > Perfil público.
        </span>
      </p>
    </div>
    <div
      v-if="doctor && Object.entries(doctor).length
        && showChat"
      class="chat-container"
    >
      <div class="chat-header">
        <p>
          Soy {{ botName }}, pregúntame.
        </p>
        <mdb-btn
          flat
          dark-waves
          icon="times"
          class="p-1 m-0"
          @click="showChat = false"
        />
      </div>
      <div class="chat-body">
        <div class="answer">
          <div class="bub">
            <div
              class="message"
            >
              Pregúntame algo dirigido a {{ personNames.fullName }},
              puedo responderte sobre sus horarios, servicios o
              cualquier otra pregunta que me haya enseñado a responder.
            </div>
          </div>
          <div
            class="chat-avatar"
          >
            <img
              v-if="botImage"
              :src="botImage"
              :alt="botName"
              class="w-3rem"
            >
            <img
              v-else
              src="../../../public/img/vectores/fab icon.svg"
              alt="AmiiBot"
              class="w-3rem"
            >
          </div>
        </div>
        <div
          v-for="(message, m) in chatMessages"
          :key="m"
        >
          <div
            :class="message.type == 'question' ? 'question' : 'answer'"
          >
            <div class="bub">
              <div
                class="message"
                v-html="markdown(message.text)"
              />
            </div>
          </div>
          <div
            v-if="message.type == 'answer'"
            class="chat-avatar"
          >
            <img
              v-if="botImage"
              :src="botImage"
              :alt="botName"
              class="w-3rem"
            >
            <img
              v-else
              src="../../../public/img/vectores/fab icon.svg"
              alt="AmiiBot"
              class="w-3rem"
            >
          </div>
        </div>
      </div>
      <div class="chat-input">
        <mdb-input
          v-model="chatMessage"
          type="text"
          class="col p-0 my-0"
          outline
          :disabled="btnDisabled"
          @keyup.enter="sendQuestion()"
        />
        <mdb-btn
          flat
          dark-waves
          :icon="btnDisabled ? 'circle-notch' : 'paper-plane'"
          class="p-1 mt-0 btn-outline-primary-base"
          :icon-class="btnDisabled ? 'fa-spin' : ''"
          :disabled="btnDisabled"
          @click="sendQuestion()"
        />
      </div>
    </div>
    <template
      v-else-if="doctor && Object.entries(doctor).length"
    >
      <mdb-btn
        dark-waves
        class="fab-btn"
        @click="showChat = true"
      >
        <img
          v-if="botImage"
          :src="botImage"
          :alt="botName"
          class="w-3rem"
        >
        <img
          v-else
          src="../../../public/img/vectores/fab icon.svg"
          alt="AmiiBot"
          class="w-3rem"
        >
      </mdb-btn>
      <p class="fab-text">
        <span>
          ¿Tienes alguna duda?
        </span>
      </p>
    </template>
    <ModalAgendarCita
      :show-modal="showAgendarCita"
      :doctor="doctor"
      @close="showAgendarCita = $event"
    />
  </div>
</template>
<script>
import {mdbBtn, mdbInput} from 'mdbvue';
import ModalAgendarCita from '@/components/modalAgendarCita.vue';
import SeccionEnlaces from '@/components/SeccionEnlaces.vue';
import {capitalizeAll} from '@/utils/validations.js';
import grapesjs from 'grapesjs/dist/grapes.min.js';
const gjsBlocksBasic = require('grapesjs-blocks-basic');
const gjsTabs = require('grapesjs-tabs');
import DOMPurify from 'dompurify';
import {marked} from 'marked';
import gql from 'graphql-tag';
import doctorProfileGql from '@/graphql/doctorProfile.gql';
import businessPublicGql from '@/graphql/businessPublic.gql';
import doctorReviewsGql from '@/graphql/doctorReviews.gql';
import publicProfileByAllyGql from '@/graphql/publicProfileByAlly.gql';

const imgPath = process.env.NODE_ENV === 'production'
  ? process.env.VUE_APP_MULTIMEDIA_HTTPS
  : process.env.VUE_APP_MULTIMEDIA_HTTP;

const amiibotUrl = process.env.NODE_ENV === 'production'
  ? process.env.VUE_APP_AMIIBOT_HTTPS
  : process.env.VUE_APP_AMIIBOT_HTTP;
export default {
  name: 'PerfilDeUsuario',
  components: {
    mdbBtn,
    mdbInput,
    ModalAgendarCita,
    SeccionEnlaces,
  },
  data() {
    return {
      doctor: {
        profileInfo: {
          wysiwyg: '',
          nameBot: 'AmiiBot',
        },
        contactsInfo: [],
        works_at: [],
      },
      showChat: false,
      btnDisabled: false,
      chatMessage: '',
      chatMessages: [],
      showAgendarCita: false,
      addBtnCitaAnimation: false,
      imgPath: imgPath + 'dir/',
      wuid: '',
      marked,
      DOMPurify,
      htmlContent: '',
      cssContent: '',
      doctorReviews: {
        average: 0,
        totalCount: 0,
        distribution: [0, 0, 0, 0, 0],
        avgCalidadAtencion: null,
        avgComodidadEspacios: null,
        avgTiempoEspera: null,
        avgPrecisionResultado: null,
        reviews: [],
      },
      allyBranding: null,
      doctorProfileUrlFromAlly: '',
      isAllyChecked: false,
      loadingMoreReviews: false,
    };
  },
  computed: {
    isReferralRoute() {
      return !!(
        this.$route?.params?.refUsername
        && this.$route?.params?.allyProfileUrl
      );
    },
    effectiveProfileUrl() {
      if (this.$route?.params?.usuario) {
        return this.$route.params.usuario.toLowerCase();
      }
      if (this.doctorProfileUrlFromAlly) {
        return this.doctorProfileUrlFromAlly.toLowerCase();
      }
      return '';
    },
    isDoctorLoading() {
      if (
        this.isReferralRoute
        && !this.doctorProfileUrlFromAlly
        && !this.isAllyChecked
      ) {
        return true;
      }
      return !!(
        this.$apolloData?.queries?.doctor?.loading
        || this.$apolloData?.queries?.profileByAlly?.loading
        || this.$apolloData?.queries?.business?.loading
      );
    },
    isReviewsLoading() {
      return !!(this.$apolloData?.queries?.doctorReviews?.loading);
    },
    hasSubcriteriaAverages() {
      if (!this.doctorReviews) return false;
      return !!(
        this.doctorReviews.avgCalidadAtencion
        || this.doctorReviews.avgComodidadEspacios
        || this.doctorReviews.avgTiempoEspera
        || this.doctorReviews.avgPrecisionResultado
      );
    },
    isDarkBg() {
      return this.getLuminance(this.bgColor) < 130;
    },
    isDarkBtn() {
      return this.getLuminance(this.btnBgColor) < 130;
    },
    reviewsBorderColor() {
      return this.isDarkBg
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.1)';
    },
    starTrackBg() {
      return this.isDarkBg
        ? 'rgba(255, 255, 255, 0.16)'
        : 'rgba(0, 0, 0, 0.08)';
    },
    bigRatingColor() {
      if (this.isDarkBg) {
        if (this.isDarkBtn) {
          return this.txtColor || '#ffffff';
        }
        return this.btnBgColor;
      }
      if (!this.isDarkBtn) {
        return this.txtColor || '#212529';
      }
      return this.btnBgColor;
    },
    reviewsSectionStyle() {
      return {
        borderTop: `1px solid ${this.reviewsBorderColor}`,
        color: this.txtColor,
      };
    },
    reviewsCardStyle() {
      const bg = this.isDarkBg
        ? 'rgba(255, 255, 255, 0.07)'
        : 'rgba(0, 0, 0, 0.03)';
      return {
        backgroundColor: bg,
        border: `1px solid ${this.reviewsBorderColor}`,
        color: this.txtColor,
      };
    },
    subcriteriaBadgeStyle() {
      const bg = this.isDarkBg
        ? 'rgba(255, 255, 255, 0.09)'
        : 'rgba(0, 0, 0, 0.04)';
      return {
        backgroundColor: bg,
        border: `1px solid ${this.reviewsBorderColor}`,
      };
    },
    reviewChipStyle() {
      const bg = this.isDarkBg
        ? 'rgba(255, 255, 255, 0.12)'
        : 'rgba(0, 0, 0, 0.05)';
      return {
        backgroundColor: bg,
        border: `1px solid ${this.reviewsBorderColor}`,
        color: `${this.txtColor} !important`,
      };
    },
    authorAvatarStyle() {
      return {
        backgroundColor: this.btnBgColor,
        color: `${this.btnColor} !important`,
      };
    },
    loadMoreBtnStyle() {
      const bColor = this.doctor?.profileInfo?.btnBorderColor
        || this.btnBgColor;
      return {
        backgroundColor: this.btnBgColor,
        color: `${this.btnColor} !important`,
        border: `1px solid ${bColor}`,
      };
    },
    bgColor() {
      return this.doctor?.profileInfo?.bgColor
        ? this.doctor.profileInfo.bgColor
        : 'rgb(255, 255, 255)';
    },
    btnBgColor() {
      return this.doctor?.profileInfo?.btnBgColor
        ? this.doctor.profileInfo.btnBgColor
        : this.doctor?.profileInfo?.txtColor
          ? this.doctor.profileInfo.txtColor
          : '#002196';
    },
    btnColor() {
      return this.doctor?.profileInfo?.btnColor
        ? this.doctor.profileInfo.btnColor
        : this.doctor?.profileInfo?.bgColor
          ? this.doctor.profileInfo.bgColor
          : 'rgb(255, 255, 255)';
    },
    imgSrc() {
      if (this.doctor?.profileInfo?.image?.url) {
        return this.imgPath + this.doctor.profileInfo.image.url;
      }
      if (this.doctor?.profileInfo?.avatar) {
        return `https://www.gravatar.com/avatar/${
          this.doctor.profileInfo.avatar}?s=150`;
      }
      return 'https://www.gravatar.com/avatar/?s=150&d=mp';
    },
    txtColor() {
      return this.doctor?.profileInfo?.txtColor
        ? this.doctor.profileInfo.txtColor
        : 'rgb(33, 37, 41)';
    },
    contactListLength() {
      const longitud = this.doctor?.contactsInfo?.length || 0;
      return longitud > 2 ? 'columns3' : longitud == 2 ? 'columns2' : '';
    },
    personNames() {
      return {
        names: `${this.doctor.prename ? this.doctor.prename + ' ' : ''}`
          + `${this.doctor.fname}`
          + `${this.doctor.mname ? ' ' + this.doctor.mname : ''}`,
        lnames: `${this.doctor.lname}`
          + `${this.doctor.llname ? ' ' + this.doctor.llname : ''}`
          + `${this.doctor.posname ? ' ' + this.doctor.posname : ''}`,
        fullName: `${this.doctor.prename ? this.doctor.prename + ' ' : ''}`
          + `${this.doctor.fname}`
          + `${this.doctor.mname ? ' ' + this.doctor.mname : ''}`
          + ` ${this.doctor.lname}`
          + `${this.doctor.llname ? ' ' + this.doctor.llname : ''}`
          + `${this.doctor.posname ? ' ' + this.doctor.posname : ''}`,
      };
    },
    botName() {
      let name = 'AmiiBot';
      if (
        this.doctor
        && this.doctor.profileInfo
        && this.doctor.profileInfo.nameBot
      ) {
        name = this.doctor.profileInfo.nameBot;
      }
      return name;
    },
    botImage() {
      let botImg = null;
      if (
        this.doctor.profileInfo
        && this.doctor.profileInfo.avatarBot
      ) {
        botImg = this.imgPath + this.doctor.profileInfo.avatarBot;
      }
      return botImg;
    },
  },
  watch: {
    doctor() {
      if (
        this.doctor
        && this.doctor.profileInfo
        && this.doctor.profileInfo.wysiwyg
      ) {
        this.loadDesign();
      }
    },
  },
  mounted() {
    this.addBtnCitaAnimation = true;
    this.animationTimeout();
    if (typeof localStorage !== 'undefined') {
      if (!localStorage.getItem('wuid')) {
        try {
          const wuid = self.crypto.randomUUID();
          this.wuid = wuid;
          localStorage.setItem('wuid', wuid);
        } catch (error) {
          console.log('Navegador no actualizado para UUID');
        }
      } else {
        this.wuid = localStorage.getItem('wuid');
      }
    }
  },
  methods: {
    searchTags() {
      let tags = '';
      if (this.doctor.profileInfo
        && this.doctor.profileInfo.searchTags
        && this.doctor.profileInfo.searchTags.length
        && this.doctor.profileInfo.searchTags[0].name) {
        this.doctor.profileInfo.searchTags.map((tag) => {
          if (tags.length > 0) tags += ', ';
          tags += capitalizeAll(tag.name);
        });
      }
      return tags;
    },
    mapBusinessToDoctor(business) {
      if (!business) return null;
      return {
        profileUrl: business.profileUrl || '',
        prename: '',
        fname: business.name,
        mname: '',
        lname: '',
        llname: '',
        posname: '',
        contactsInfo: business.contactsInfo || [],
        profileInfo: {
          ...(business.profileInfo || {}),
          searchTags: business.profileInfo?.searchTags || [],
        },
        works_at: (business.medicalInstitutes || []).map((mi) => ({
          id: mi.id,
          in: {
            name: mi.name || '',
            location: {
              name: mi.name || '',
              address: mi.address || {},
            },
          },
          schedule: [],
          tels: [],
        })),
        isBusiness: true,
        businessId: business.id,
      };
    },
    async sendQuestion() {
      if (this.chatMessage.trim()) {
        const chat = document.querySelector('.chat-body');
        this.chatMessages.push({
          type: 'question',
          text: this.chatMessage,
        });
        this.$nextTick(() => {
          chat.scrollTo(0, chat.scrollHeight);
        });

        const myHeaders = new Headers();
        myHeaders.append('Content-Type', 'application/json');
        try {
          this.btnDisabled = true;
          await fetch(
              amiibotUrl + this.effectiveProfileUrl, {
                method: 'post',
                headers: myHeaders,
                body: JSON.stringify({
                  pregunta: this.chatMessage,
                  uid: this.wuid,
                  c: 'p',
                }),
              },
          )
              .then((res) => res.json())
              .then((json) => {
                this.chatMessage = '';

                this.chatMessages.push({
                  type: 'answer',
                  text: json.respuesta
                  ? json.respuesta
                  : 'No me permiten responder ahora. '
                  + 'Usa otro medio de contacto, disculpa.',
                });
              })
              .catch(() => {
                this.chatMessages.push({
                  type: 'answer',
                  text: 'Intenta enviar de nuevo, hubo un error.'
                  + ' También puedes comunicarte por los medios de'
                  + ' contacto visibles en el perfil.',
                });
              }).finally(() => {
                this.btnDisabled = false;
                this.$nextTick(() => {
                  chat.scrollTo(0, chat.scrollHeight);
                });
              });
        } catch (error) {
          // console.log(error);
          this.btnDisabled = false;
        }
      }
    },
    animationTimeout() {
      setTimeout(() => {
        if (document.querySelector('.fab-text')) {
          document.querySelector('.fab-text').classList.toggle('animation');
        }
        this.animationTimeout();
      }, 4000);
    },
    markdown(text) {
      // eslint-disable-next-line new-cap
      return this.marked.parse(this.DOMPurify
          .sanitize(text), {mangle: false, headerIds: false,
        breaks: true,
        gfm: true});
    },
    async loadDesign() {
      if (!this.doctor?.profileInfo?.wysiwyg) {
        this.htmlContent = '';
        this.cssContent = `.amii-agendar-btn {background-color:${this.btnBgColor
        } !important;color:${this.btnColor};font-weight:bold;}`;
        return;
      }
      let projectToLoad;
      try {
        projectToLoad = JSON.parse(this.doctor.profileInfo.wysiwyg);
        const editor = await grapesjs.init({
          container: '#ge',
          readOnly: true,
          plugins: [
            gjsBlocksBasic,
            gjsTabs,
          ],
        });

        editor.loadProjectData(projectToLoad);
        const fontsHtml = projectToLoad.customFontsHtml || '';
        const wrapperId = editor.getWrapper()?.getId();

        let generatedCss = editor.getCss() || '';
        if (wrapperId) {
          const wrapperRegex = new RegExp(`#${wrapperId}\\b`, 'g');
          generatedCss = generatedCss.replace(wrapperRegex, '#wrapper');
        }
        generatedCss += `
          #wrapper .gjs-cell,
          #wrapper .gjs-cell30,
          #wrapper .gjs-cell70 {
            height: auto !important;
            min-height: auto !important;
          }
          @media (max-width: 768px) {
            #wrapper .gjs-row {
              display: block !important;
              width: 100% !important;
            }
            #wrapper .gjs-cell,
            #wrapper .gjs-cell30,
            #wrapper .gjs-cell70 {
              display: block !important;
              width: 100% !important;
              max-width: 100% !important;
              box-sizing: border-box;
              margin-bottom: 1rem;
            }
          }
        `;

        // Guardamos el resultado en las variables que se muestran en el HTML
        this.htmlContent = editor.getHtml() + fontsHtml;
        this.cssContent = generatedCss
          + ` .amii-agendar-btn {background-color:${this.btnBgColor
          } !important;color:${this.btnColor};font-weight:bold;}`;
        const scriptExecution = new Function(editor.getJs());
        // Ex Js
        this.$nextTick(() => {
          scriptExecution();
        });
        // Cerramos el editor invisible para no gastar memoria
        editor.destroy?.();
      } catch (error) {
        // console.log(error);
        this.htmlContent = this.markdown(this.doctor.profileInfo.wysiwyg);
        this.cssContent = `.amii-agendar-btn {background-color:${this.btnBgColor
        } !important;color:${this.btnColor};font-weight:bold;}`;
      }
    },
    getStarIcon(avg, star) {
      const rate = avg || 0;
      if (star <= Math.floor(rate)) {
        return ['fas', 'star'];
      } else if (star - 0.5 <= rate) {
        return ['fas', 'star-half-alt'];
      }
      return ['far', 'star'];
    },
    getDistributionCount(star) {
      if (!this.doctorReviews || !this.doctorReviews.distribution) return 0;
      return this.doctorReviews.distribution[star - 1] || 0;
    },
    getDistributionPercent(star) {
      if (!this.doctorReviews || !this.doctorReviews.totalCount) return 0;
      const count = this.getDistributionCount(star);
      return Math.round((count / this.doctorReviews.totalCount) * 100);
    },
    hasReviewSubcriteria(rev) {
      return !!(
        rev.calidadAtencion
        || rev.comodidadEspacios
        || rev.tiempoEspera
        || rev.precisionResultado
      );
    },
    formatReviewDate(dateVal) {
      if (!dateVal) return '';
      const str = typeof dateVal === 'object' && dateVal.formatted
        ? dateVal.formatted
        : dateVal;
      const d = new Date(str);
      if (isNaN(d.getTime())) return String(str);
      return d.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
    getLuminance(colorStr) {
      if (!colorStr || typeof colorStr !== 'string') return 255;
      let r = 255;
      let g = 255;
      let b = 255;
      const s = colorStr.trim();
      if (s.startsWith('#')) {
        let hex = s.replace('#', '');
        if (hex.length === 3) {
          hex = hex.split('').map((c) => c + c).join('');
        }
        if (hex.length === 6) {
          r = parseInt(hex.substring(0, 2), 16);
          g = parseInt(hex.substring(2, 4), 16);
          b = parseInt(hex.substring(4, 6), 16);
        }
      } else if (s.startsWith('rgb')) {
        const match = s.match(/\d+/g);
        if (match && match.length >= 3) {
          r = parseInt(match[0], 10);
          g = parseInt(match[1], 10);
          b = parseInt(match[2], 10);
        }
      }
      return (r * 299 + g * 587 + b * 114) / 1000;
    },
    loadMoreReviews() {
      this.loadingMoreReviews = true;
      const currentCount = (this.doctorReviews?.reviews?.length || 0);
      const userParam = this.effectiveProfileUrl;
      this.$apollo.queries.doctorReviews.fetchMore({
        variables: {
          profileUrl: userParam,
          first: 10,
          offset: currentCount,
        },
        updateQuery: (previousResult, {fetchMoreResult}) => {
          this.loadingMoreReviews = false;
          if (!fetchMoreResult || !fetchMoreResult.doctorReviews) {
            return previousResult;
          }
          return {
            doctorReviews: {
              ...previousResult.doctorReviews,
              ...fetchMoreResult.doctorReviews,
              reviews: [
                ...(previousResult.doctorReviews.reviews || []),
                ...(fetchMoreResult.doctorReviews.reviews || []),
              ],
            },
          };
        },
      }).catch(() => {
        this.loadingMoreReviews = false;
      });
    },
  },
  apollo: {
    profileByAlly() {
      return {
        query: gql`${publicProfileByAllyGql}`,
        variables() {
          return {
            refUsername: this.$route?.params?.refUsername || '',
            allyProfileUrl: this.$route?.params?.allyProfileUrl || '',
          };
        },
        skip() {
          return !this.$route?.params?.refUsername
            || !this.$route?.params?.allyProfileUrl;
        },
        update: (data) => data.publicProfileByAlly,
        result({data}) {
          this.isAllyChecked = true;
          const res = data?.publicProfileByAlly;
          if (res) {
            if (res.doctorProfileUrl) {
              this.doctorProfileUrlFromAlly = res.doctorProfileUrl;
            }
            if (res.allyBranding) {
              this.allyBranding = res.allyBranding;
            }
          }
        },
        error() {
          this.isAllyChecked = true;
        },
        fetchPolicy: 'network-only',
      };
    },
    doctor() {
      return {
        query: gql`${doctorProfileGql}`,
        variables() {
          return {
            usuario: this.effectiveProfileUrl,
          };
        },
        skip() {
          return !this.effectiveProfileUrl;
        },
        update: (data) => data.Doctor[0],
      };
    },
    doctorReviews() {
      return {
        query: gql`${doctorReviewsGql}`,
        variables() {
          const u = this.effectiveProfileUrl;
          const doctorId = this.doctor?.id || null;
          return {
            profileUrl: u || (this.doctor?.profileUrl
              ? this.doctor.profileUrl.toLowerCase() : null),
            doctorId,
            first: 5,
            offset: 0,
          };
        },
        skip() {
          if (!this.effectiveProfileUrl && !this.doctor?.id) return true;
          return false;
        },
        update: (data) => data.doctorReviews,
      };
    },
    business() {
      return {
        query: gql`${businessPublicGql}`,
        variables() {
          return {
            profileUrl: this.effectiveProfileUrl,
          };
        },
        skip() {
          if (!this.effectiveProfileUrl) return true;
          // Only fetch if the Doctor query returned empty
          return !!(this.doctor && this.doctor.fname);
        },
        update: (data) => data.BusinessPublic?.[0] || null,
        result({data}) {
          const business = data?.BusinessPublic?.[0];
          if (business && !(this.doctor && this.doctor.fname)) {
            this.doctor = this.mapBusinessToDoctor(business);
          }
        },
        fetchPolicy: 'network-only',
      };
    },
  },
};
</script>
<style lang="scss">
  // family className content weight rotation
  $links-type: (
    "Font Awesome 5 Brands" "facebook" "\f09a" 0 false,
    "Font Awesome 5 Brands" "instagram" "\f16d" 0 false,
    "Font Awesome 5 Brands" "linkedIn" "\f08c" 0 false,
    "Font Awesome 5 Brands" "telegram" "\f2c6" 0 false,
    "Font Awesome 5 Brands" "tiktok" "\e07b" 0 false,
    "Font Awesome 5 Brands" "twitter" "\f099" 0 false,
    "Font Awesome 5 Brands" "whatsApp" "\f232" 0 false,
    "Font Awesome 5 Brands" "youtube" "\f167" 0 false,
    "Font Awesome 5 Free" "book" "\f02d" 900 false,
    "Font Awesome 5 Free" "email" "\f0e0" 400 false,
    "Font Awesome 5 Free" "file" "\f15b" 900 false,
    "Font Awesome 5 Free" "filePdf" "\f1c1" 900 false,
    "Font Awesome 5 Free" "link" "\f0c1" 900 false,
    "Font Awesome 5 Free" "phone" "\f879" 900 rotate(80deg),
  );

  /*
    .user-profile
    Establece altura mínima de la section para poder aplicar
    el color de fondo en toda la pantalla
  */
  .user-profile {
    min-height: calc(100vh - 48px);
    padding: 1.5rem 0 1rem;

    strong {
      font-weight: 800;
    }
  }

  .user-personal-info {
    text-align: center;

    img {
      max-width: 150px;
    }

    @media screen and (min-width: 768px) {
      align-items: center;
      display: flex;
      justify-content: center;
      text-align: left;
    }

    .fs-12{
      font-size: 1.2rem;
    }
  }

  .bg-card {
    background-color: rgba(191, 187, 187, 0.2);
    border-radius: 5px;
    margin: 1.5rem 0;
    padding: 1rem;
  }

  .contact-list {
    list-style-type: none;

    @media screen and (min-width: 768px) {
      display: flex;
      justify-content: space-between;
      flex-flow: row wrap;
    }

    .contact {
      margin: .5rem 0 .5rem 26px;
      position: relative;

      &::before {
        position: absolute;
        left: -20px;
      }

      @each $family, $class, $content, $weight, $transform in $links-type {
        &.#{$class}::before {
          content: $content;
          font-family: $family;

          @if $weight !=0 {
            font-weight: $weight;
          }

          @if $transform !=false {
            transform: $transform;
          }
        }
      }

      &.split-2 {
        width: 40%;
      }

      &.split-3 {
        width: 30%;
      }

      .title {
        display: block;
        font-weight: 700;
      }
    }
  }

  .currentColor {
    color: currentColor;
  }

  .fab-btn {
    background-color: $gris-claro-home !important;
    border: 1px solid rgba(128, 128, 128, 0.2);
    border-radius: 3rem;
    bottom: .5rem;
    margin: 0;
    right: .5rem;
    padding: .5rem;
    position: fixed;
  }

  .fab-text {
    display: flex;
    bottom: 3rem;
    flex-direction: column;
    position: fixed;
    right: 3rem;

    &.animation {
      animation: rotate-scale-up 0.65s linear;
    }

    span {
      background-color: $gris-claro-home !important;
      border: 1px solid rgba(128, 128, 128, 0.2);
      border-radius: .5rem;
      padding: .5rem;
    }

    &::after {
      align-self: flex-end;
      content: "\f0d7";
      color: rgba(128, 128, 128, 0.2);
      font-family: "Font Awesome 5 Free";
      font-weight: 900;
      margin-right: .5rem;
      margin-top: -10px;
    }
  }

  .chat-container {
    background-color: $gris-claro-home !important;
    border: 1px solid rgba(128, 128, 128, 0.2);
    border-radius: 1rem 1rem 0 0;
    bottom: 0;
    right: 0;
    margin: 0 .5rem;
    position: fixed;
    overflow: hidden;

    @media (min-width: 576px) {
      right: .5rem;
      width: 285px;
    }

    .chat-header {
      padding: .5rem;
      background-color: $primary-color;
      display: flex;
      justify-content: space-between;

      p,
      button:hover {
        color: #ffff;
        font-weight: bold;
        margin: 0;
      }

      button {
        color: rgba(10, 10, 10, 0.488);
        border: 1px solid rgba(56, 56, 56, 0.2);
      }
    }

    .chat-avatar {
      background-color: #ffff;
      border-radius: 50%;
      padding: .3rem;
      width: fit-content;

      img {
        height: 1.7rem;
        width: 1.7rem;
      }
    }

    .chat-body {
      max-height: 20rem;
      overflow-y: scroll;

      div {
        padding: 0 .5rem 0 .5rem;
        display: flex;
        flex-direction: column;
        div {
          display: flex;
          flex-direction: column;
          width: fit-content;
          margin: 0;

          .message {
            font-size: .9rem;
            padding: .5rem;
            margin: 0;
            margin-top: .5rem;
            border: 1px solid rgba(128, 128, 128, 0.2);
            border-radius: .5rem;
          }

          &.question {
            align-self: flex-end;

            &::after {
              align-self: flex-end;
              content: "\f0d7";
              color: rgba(128, 128, 128, 0.2);
              font-family: "Font Awesome 5 Free";
              font-weight: 900;
              margin-right: .5rem;
              margin-top: -10px;
            }
          }

          &.answer {
            &::after {
              content: "\f0d7";
              color: rgba(128, 128, 128, 0.2);
              font-family: "Font Awesome 5 Free";
              font-weight: 900;
              margin-left: 1rem;
              margin-top: -9px;
            }
          }
        }
      }
    }

    .chat-input {
      padding: .5rem;
      display: flex;
    }
  }

  .w-3rem {
    width: 3rem;
  }

  @keyframes rotate-scale-up {
  0% {
    transform: scale(1) rotateZ(0);
  }

  25% {
    transform: scale(1.1) rotateZ(40deg);
  }

  50% {
    transform: scale(1.1) rotateZ(0deg);
  }

  75% {
    transform: scale(1.1) rotateZ(40deg);
  }

  100% {
    transform: scale(1) rotateZ(0);
  }
}

  .reviews-section {
    .rating-big-num {
      line-height: 1;
    }

    @media (min-width: 768px) {
      .border-md-right {
        border-right: 1px solid rgba(128, 128, 128, 0.2);
      }
    }

    .star-bar-row {
      .star-label {
        width: 32px;
      }
      .star-percent {
        width: 35px;
      }
      .star-progress {
        height: 8px;
        border-radius: 4px;
      }
    }

    .avatar-author {
      width: 38px;
      height: 38px;
      font-weight: 700;
      font-size: 1rem;
    }

    .subcriteria-badge {
      border-radius: 6px;
    }
  }

  .section-ally-branding {
    opacity: 0.9;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }

    .ally-branding-logo {
      max-height: 44px;
      max-width: 140px;
      object-fit: contain;
    }

    .ally-branding-text {
      font-size: 0.85rem;
      letter-spacing: 0.3px;
    }
  }

  #wrapper {
    .gjs-cell,
    .gjs-cell30,
    .gjs-cell70 {
      height: auto !important;
      min-height: auto !important;
    }

    @media (max-width: 768px) {
      .gjs-row {
        display: block !important;
        width: 100% !important;
      }

      .gjs-cell,
      .gjs-cell30,
      .gjs-cell70 {
        display: block !important;
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box;
        margin-bottom: 1rem;
      }
    }
  }
</style>
